import { useState } from 'react'
import type { MeetingPrep } from '../../types'

/**
 * Human-authored meeting prep. Saving all-blank fields clears the prep — same behaviour as the
 * prototype's "Remove prep" button, which is why there's no separate delete call.
 */
export function MeetingPrepEditor({
  company,
  prep,
  onSave,
  onCancel,
}: {
  company: string
  prep?: MeetingPrep
  onSave: (prep: MeetingPrep) => void
  onCancel: () => void
}) {
  const [attendee, setAttendee] = useState(prep?.attendee ?? '')
  const [role, setRole] = useState(prep?.role ?? '')
  const [meta, setMeta] = useState(prep?.meta ?? '')
  const [notes, setNotes] = useState(prep?.notes ?? '')

  function uploadNotes(file: File) {
    const reader = new FileReader()
    reader.onload = () => setNotes((n) => (n ? `${n}\n` : '') + String(reader.result ?? ''))
    reader.readAsText(file)
  }

  return (
    <div
      id="prepEditor"
      style={{
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderLeft: '4px solid var(--accent)',
        borderRadius: 14,
        padding: '18px 20px',
        marginBottom: 18,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '1.4px',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          marginBottom: 10,
        }}
      >
        Meeting prep / notes{' '}
        <span style={{ color: 'var(--slate)', textTransform: 'none', letterSpacing: 0, fontWeight: 600 }}>
          · {company}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="fld" htmlFor="prep-att">
            Who you're meeting (optional)
          </label>
          <input
            className="tx"
            id="prep-att"
            value={attendee}
            onChange={(e) => setAttendee(e.target.value)}
            placeholder="e.g. Jane Smith"
            autoComplete="off"
          />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="fld" htmlFor="prep-role">
            Role / title (optional)
          </label>
          <input
            className="tx"
            id="prep-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="e.g. Head of Data"
            autoComplete="off"
          />
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <label className="fld" htmlFor="prep-meta">
          Meeting details (optional)
        </label>
        <input
          className="tx"
          id="prep-meta"
          value={meta}
          onChange={(e) => setMeta(e.target.value)}
          placeholder="e.g. 24 June, 2pm, their offices — second meeting"
          autoComplete="off"
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <label className="fld" htmlFor="prep-notes">
          Notes — paste anything: priorities, context, what to say, questions. Start a line with “-” for a
          bullet.
        </label>
        <textarea
          className="tx"
          id="prep-notes"
          rows={8}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
        <button
          className="genbtn"
          id="prep-save"
          style={{ margin: 0, padding: '10px 20px', fontSize: 14 }}
          onClick={() => onSave({ attendee, role, meta, notes })}
        >
          Save to brief
        </button>

        <label className="abtn" style={{ cursor: 'pointer', margin: 0 }}>
          ↑ Upload .txt
          <input
            type="file"
            accept=".txt,.md,.csv,text/plain"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) uploadNotes(f)
              e.target.value = ''
            }}
          />
        </label>

        <button
          className="abtn"
          style={{ margin: 0 }}
          onClick={() => onSave({ attendee: '', role: '', meta: '', notes: '' })}
        >
          Remove prep
        </button>
        <button className="abtn" style={{ margin: 0 }} onClick={onCancel}>
          Cancel
        </button>

        <span style={{ fontSize: 12, color: 'var(--slate)', flex: 1, minWidth: 200 }}>
          Attaches a ★ Meeting Prep panel to the top of this brief. Saved with the brief and shared with the
          team.
        </span>
      </div>
    </div>
  )
}
